import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
    createWaistMeasurement,
    updateWaistMeasurement
} from '../../../../store/participantService';
import { todayDate } from '../../../../utils/dateFunctions';
import { regExps } from '../../../../utils/formValidation/validationPatterns';
import DateSelect from '../../../formElements/DateSelect';
import TextInputField from '../../../formElements/TextInputField';
import FormActions from '../../../ui/formActions/FormActions';

import { validate } from './validateWaistMeasurements';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const CreateWaistMeasurement = ({ participant, onClose, isDisabled, formType, row = {} }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const _MESSAGE = `Participant waist measurement entry has been ${formType === 'create' ? 'added' : 'updated'}`;
    const initialState = {
        id: '',
        participantId: '',
        measurement: '',
        measurementDate: todayDate()
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialRow, setInitialRow] = useState({});
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // USE EFFECTS
    useEffect(() => {
        if (formType === 'edit') {
            setNewEntry(row);
            setInitialRow(row);
        }
    }, [row]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onMeasurementChange = (e) => {
        onUpdate('measurement', e.target.value.replace(regExps.DIGITS_R, ''));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, formType, initialRow);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = { ...newEntry, participantId: participant?.id };
        formType === 'create'
            ? dispatch(createWaistMeasurement(payload, _MESSAGE))
            : dispatch(updateWaistMeasurement(payload.id, payload, _MESSAGE));
        onClose();
    };

    // RENDER
    return (
        <div className={`${form.formWrapper} ${app.fadein}`}>
            <form
                className={form.form}
                onSubmit={onSubmit}
                data-testid="edit_waist_measurement_form">
                <h2 className={app.sectionHeading}>
                    {formType === 'create' ? 'Add New Waist Measurement' : 'Edit Waist Measurement'}
                </h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextInputField
                            id="measurement"
                            label="Measurement (cm)"
                            placeholder="Enter measurement..."
                            disabled={isDisabled}
                            mandatory={true}
                            maxLength={3}
                            value={newEntry.measurement}
                            error={errors.measurement}
                            onChange={onMeasurementChange}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <DateSelect
                            value={newEntry.measurementDate}
                            label="Event Date"
                            disabled={isDisabled}
                            mandatory={true}
                            error={errors.measurementDate}
                            onDateChange={(date) => onUpdate('measurementDate', date)}
                        />
                    </div>
                </div>

                <FormActions
                    onClose={onSubmit}
                    onCancel={onClose}
                    disabled={isButtonDisabled || isDisabled}
                    btnText={formType === 'create' ? 'Save' : 'Update'}
                    customClass="noTopBorder"
                    error={errors?.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

CreateWaistMeasurement.propTypes = {
    participant: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    formType: PropTypes.string,
    row: PropTypes.object
};

export default CreateWaistMeasurement;
